<template>
  <HeaderBar
    class="hide-for-medium"
    :modifiers="['dark', 'sticky']"
  >
    <router-link
      :aria-label="$t('general.back')"
      class="header-bar__title typo-nav-item"
      :to="{ name: 'shaftJobs', params: { scope: 'jobs' } }"
    >
      <UseSvg
        class="icon"
        name="arrow_left"
      />

      <div class="typo-small-title">
        {{ $t('nav.maintenance') }}
      </div>
    </router-link>
  </HeaderBar>

  <HeaderBar
    v-if="!loading"
    :modifiers="['light', 'stack-tablet-below', 'left-align']"
  >
    <div class="header-bar__margin-y">
      <strong class="typo-title">{{ $t('shafts.jobs.list_of_work_orders') }}</strong>
      <div class="header-bar__sub-title typo-tiny capitalize">
        {{ `#${renderShortId(job.id)}` }}
      </div>
    </div>
    <template #right>
      <template v-if="job.status === 'provisionally_scheduled'">
        <Select
          v-if="job.policy.update"
          class="select--input select--input__margin-y"
          :items="maintenanceActionOptions"
          :modelValue="job.maintenance_action"
          :placeholder="$t('shafts.jobs.select_maintenance_action')"
          @hover="hoverMaintenanceAction = $event"
          @update:modelValue="updateField('maintenance_action', $event)"
        />
        <span v-else-if="job.maintenance_action">
          {{ $t(`shafts.maintenance_actions.${job.maintenance_action}`) }}
        </span>
        <Popper
          v-if="job.maintenance_action"
          v-model:open="detMechsPopupOpen"
        >
          <template #trigger>
            <button
              class="info-button"
              type="button"
            >
              <IEncircled />
            </button>
          </template>

          <div class="det-mechs-tooltip">
            <button
              class="button button--small button--transparent"
              type="button"
              @click="detMechsPopupOpen = false"
            >
              <UseSvg
                class="icon icon--small button__icon"
                name="close"
              />
            </button>
            <div class="det-mechs-title typo-small-title">
              {{ `“${$t(`shafts.maintenance_actions.${job.maintenance_action}`)}” ${$t('shafts.jobs.will_address_the_following')}:` }}
            </div>
            <ul class="det-mechs-list">
              <li
                v-for="detMech in detMechsForMaintenanceAction()"
                :key="detMech"
                class="typo-small"
              >
                {{ $t(`deterioration_mechanisms.${detMech}.name`) }}
              </li>
            </ul>
          </div>
        </Popper>
        <button
          v-if="job.policy.transfer"
          class="button button--margin-y"
          :disabled="!canSendToPlanner"
          type="button"
          @click="sendToPlannerModalOpen = true"
        >
          {{ $t('shafts.jobs.send_to_planner') }}
        </button>
      </template>
      <template v-else-if="job.status === 'transferred_to_planner'">
        <button
          v-if="job.policy.update"
          class="button button--margin-y"
          :disabled="!hasChanges"
          type="button"
          @click="save"
        >
          <Loader v-if="saving" />
          <template v-else-if="hasChanges">
            {{ $t('shafts.jobs.save_progress') }}
          </template>
          <template v-else>
            <UseSvg
              class="icon icon--small button__icon"
              name="checkmark"
            />
            <span class="button__label">{{ $t('shafts.jobs.saved') }}</span>
          </template>
        </button>
        <button
          v-if="job.policy.complete"
          class="button button--margin-y"
          :disabled="!canComplete"
          type="button"
          @click="toggleOpenDialogAction('complete')"
        >
          {{ $t('shafts.jobs.complete') }}
        </button>
      </template>
    </template>
  </HeaderBar>

  <HeaderBar v-if="!loading">
    <div class="header-bar__title-container-details">
      <div class="header-bar__title-container-details-count ">
        <p class="typo-small typo-ignore header-bar__info">
          {{
            pluralize(
              job.work_orders.length,
              $t('activerecord.models.shafts/work_order.one'),
              $t('activerecord.models.shafts/work_order.other')
            )
          }}
        </p>
      </div>
      <!-- TODO: refactor. better modifiers -->
      <div class="header-bar__title-container-details-info header-bar__title-container-details-info--left header-bar__title-container-details-info--wrap header-bar__title-container-details-info--no-padding-right header-bar__title-container-details-info--no-padding-mobile-x">
        <p class="typo-small typo-ignore header-bar__info header-bar__info--mobile-stack header-bar__info--align-center">
          <template v-if="job.status === 'completed'">
            <div class="flex">
              <em>{{ $t('shafts.jobs.completed_on') }}</em>
              <strong class="select__label-wrapper">{{ formatDate(job.completed_at) }}</strong>
            </div>
            <div
              v-if="job?.completed_by?.full_name"
              class="flex"
            >
              <em>{{ $t('shafts.jobs.by') }}</em>
              <strong class="select__label-wrapper">{{ job.completed_by.full_name }}</strong>
            </div>
          </template>
          <template v-else>
            <em>{{ $t(`shafts.jobs.${job.status}_for`) }}</em>
            <strong>
              <DatePicker
                v-if="job.policy.update"
                :minDate="new Date()"
                :modelValue="new Date(job.date)"
                :selectModifiers="['inline']"
                @update:modelValue="updateField('date', isoDate($event))"
              />
              <span v-else>&nbsp;{{ formatDate(job.date) }}</span>
            </strong>
            <button
              v-if="job.policy.destroy"
              :aria-label="$t('general.delete')"
              class="button button--transparent button--mobile-no-margin"
              @click="toggleOpenDialogAction('delete')"
            >
              <UseSvg
                class="icon icon--small button__icon"
                name="trash_16"
              />
            </button>
            <template v-if="job.status === 'provisionally_scheduled'">
              <button
                v-if="job.policy.update"
                class="button button--small button--no-margin"
                @click="addWorkOrdersModalOpen = true"
              >
                <UseSvg
                  class="button__icon icon icon--small"
                  name="plus"
                />
                <span class="button__label">{{ $t('shafts.jobs.add_work_orders') }}</span>
              </button>
            </template>
          </template>
        </p>
      </div>
    </div>
  </HeaderBar>
  <!-- Work orders list  -->
  <div
    v-if="!loading"
    class="scroll-container scroll-container--padded-x scroll-container--no-margin"
  >
    <div class="issue-list">
      <div
        v-for="group in groups"
        :key="group.name"
      >
        <h3 class="group-name typo-ignore typo-title">
          {{ group.name }}
        </h3>
        <ul class="issue-list__issues">
          <WorkOrder
            v-for="workOrder in group.workOrders"
            :key="workOrder.id"
            :hoverDetMechs="hoverDetMechs"
            :job="job"
            :selectedDetMechs="selectedDetMechs"
            :workOrder="workOrder"
            @updateJob="saveJob"
          />
        </ul>
      </div>
    </div>
  </div>

  <Loader v-if="loading" />

  <SendToPlannerModal
    v-if="sendToPlannerModalOpen"
    :job="job"
    @dismissModal="sendToPlannerModalOpen = false"
    @save="onTransfer"
  />

  <AddWorkOrdersModal
    v-if="addWorkOrdersModalOpen"
    :job="job"
    @dismissModal="addWorkOrdersModalOpen = false"
    @save="onWorkOrdersAdd"
  />
  <Dialog
    v-if="openDialog"
    :title="dialogText"
    @dismissDialog="dismissDialogAction"
  >
    <template #actions>
      <button
        :aria-label="dialogAriaLabel"
        class="button button--inverse"
        type="button"
        @click="dialogAction"
      >
        {{ $t('general.yes') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import { watch, ref, onMounted, computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import I18n from 'i18n-js';
import cloneDeep from 'clone-deep';
import useEmitter from '@/functions/use_emitter';

// Utils
import pluralize from '@/functions/pluralize';
import { renderShortId } from '@/functions/utils.js';
import useAjax from '@/functions/use_ajax';
import {
  SHAFT_MAINTENANCE_ACTIONS,
  STATION_MAINTENANCE_ACTIONS
} from '@/functions/simm/shafts/deterioration_and_maintenance';
import { isoDate, formatDate } from '@/functions/date_format_helpers';

// Components
import HeaderBar from '@/components/generic/header_bar.vue';
import Loader from '@/components/generic/loader.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Popper from '@/components/generic/popper.vue';
import Select from '@/components/form/select.vue';
import DatePicker from '@/components/form/date_picker.vue';
import WorkOrder from '@/components/shafts/maintenance/work_order.vue';
import SendToPlannerModal from '@/components/shafts/maintenance/send_to_planner_modal.vue';
import AddWorkOrdersModal from '@/components/shafts/maintenance/add_work_orders_modal.vue';
import Dialog from '@/components/generic/dialog.vue';

import IEncircled from '@/images/i_encircled.svg';

const emit = defineEmits(['statusChange', 'updateJob', 'deleteJob']);

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
  jobId: { type: String, required: true },
  scope: { type: String, default: 'jobs' },
});

const ajax = useAjax();
const router = useRouter();
const emitter = useEmitter();

const originalJob = ref(null);
const job = reactive({
  date: null,
  planner: null,
  element_type: null,
  policy: {},
  maintenance_action: null,
  status: null,
  work_orders: [],
  activity: null
});
const hoverMaintenanceAction = ref(null);
const loading = ref(true);
const saving = ref(false);
const shaftUrl = computed(() => `/organisations/${props.organisationId}/shafts/${props.shaftId}`);
const jobUrl = computed(() => `${shaftUrl.value}/jobs/${props.jobId}`);
const sendToPlannerModalOpen = ref(false);
const addWorkOrdersModalOpen = ref(false);
const detMechsPopupOpen = ref(false);

const openDialog = ref(false);
const completeDialog = ref(false);
const deleteDialog = ref(false);

async function fetchJob() {
  if (props.jobId) {
    loading.value = true;
    const { ok, data } = await ajax(jobUrl.value);
    if (ok) {
      data.work_orders
       .filter(({ external_reference }) => !external_reference)
       .forEach(workOrder => workOrder.external_reference = '');
      originalJob.value = cloneDeep(data);
      Object.assign(job, data);
    }
    loading.value = false;
  }
}

onMounted(fetchJob);
watch(() => props.jobId, fetchJob);

const selectedDetMechs = computed(() => detMechsForMaintenanceAction(job.maintenance_action));
const hoverDetMechs = computed(() => detMechsForMaintenanceAction(hoverMaintenanceAction.value));

const canSendToPlanner = computed(() => job.work_orders.length > 0 && job.maintenance_action);

const hasChanges = computed(() => {
  if (job.status !== 'transferred_to_planner') return false;
  if (!originalJob.value) return false;

  for (let workOrder of job.work_orders) {
    let originalWorkOrder = originalJob.value.work_orders.find(({ id }) => id === workOrder.id);
    if (
      workOrder.external_reference !== originalWorkOrder.external_reference ||
      workOrder.completed !== originalWorkOrder.completed
    ) {
      return true;
    }
  }
  return false;
});

const canComplete = computed(() => {
  if (job.status !== 'transferred_to_planner') return false;
  if (!originalJob.value) return false;
  if (hasChanges.value) return false;

  for (let workOrder of job.work_orders) {
    if (!workOrder.completed) return false;
  }

  return true;
});

const groups = computed(() => {
  let result = {};

  for (let workOrder of job.work_orders) {
    let location = workOrder.activity.member.location;
    let groupName = location.shaft_element?.name || location.station?.name;

    result[groupName] = result[groupName] || [];
    result[groupName].push(workOrder);
  }

  return Object.entries(result).map(([name, workOrders]) => ({ name, workOrders }));
});

const maintenanceActionOptions = computed(() => {
  if (loading.value) return [];

  const [baseElementType, elementType] = job.element_type.split('/');

  let actions = baseElementType === 'shaft' ?
    SHAFT_MAINTENANCE_ACTIONS :
    STATION_MAINTENANCE_ACTIONS;

  return Object.keys(actions[elementType]).map(action => ({
    label: I18n.t(`shafts.maintenance_actions.${action}`),
    value: action,
  }));
});

const dialogText = computed(() => {
  return completeDialog.value ? I18n.t('shafts.jobs.completion_confirmation') : I18n.t('shafts.jobs.delete_confirmation_title');
});

const dialogAriaLabel = computed(() => {
  return completeDialog.value ? 'complete-job' : 'delete-job';
});

const dialogAction = computed(() => {
  return completeDialog.value ? complete : deleteJob;
});

function saveJob(data) {
  Object.assign(job, data);
  emit('updateJob', data);
}

async function updateField(field, value) {
  let { ok, data } = await ajax(jobUrl.value, {
    method: 'PATCH',
    body: { job: { [field]: value } }
  });

  if (ok) {
    saveJob(data);
  }
}

function formatWorkOrderForUpdate({ id, completed, external_reference }) {
  return { id, completed: completed ? true : false, external_reference };
}

async function save() {
  saving.value = true;

  let { ok, data } = await ajax(jobUrl.value, {
    method: 'PATCH',
    body: { job: { work_orders_attributes: job.work_orders.map(formatWorkOrderForUpdate) } }
  });

  if (ok) {
    originalJob.value = cloneDeep(data);
    Object.assign(job, data);
  }

  saving.value = false;
}

function onTransfer(data) {
  saveJob(data);
  sendToPlannerModalOpen.value = false;
}

function toggleOpenDialogAction(action) {
  openDialog.value = true;
  action === 'complete' ? completeDialog.value = true : deleteDialog.value = true;
}

function dismissDialogAction() {
  completeDialog.value = false;
  deleteDialog.value = false;
  openDialog.value = false;
}

async function complete() {

  let { ok, data } = await ajax(`${jobUrl.value}/completions`, { method: 'POST' });

  if (ok) {
    Object.assign(job, data);
    router.replace({ name: 'shaftJob', params: { scope: 'completed_jobs' } });
    dismissDialogAction();
    emitter.emit('toast', {
      status: 'success',
      message: I18n.t('shafts.maintenance.job_complete_toast')
    });
  } else {
    emitter.emit('toast', {
      status: 'error',
      message: I18n.t('shafts.maintenance.job_error_toast')
    });
  }
}

function onWorkOrdersAdd(data) {
  saveJob(data);
  addWorkOrdersModalOpen.value = false;
}

async function deleteJob() {
  let { ok } = await ajax(jobUrl.value, { method: 'DELETE' });

  if (ok) {
    emit('deleteJob', job);
    router.replace({ name: 'shaftJobs' });
  }
}

function detMechsForMaintenanceAction(action = job.maintenance_action) {
  if (!action) return [];

  const [baseElementType, elementType] = job.element_type.split('/');
  switch (baseElementType) {
    case 'shaft': return SHAFT_MAINTENANCE_ACTIONS[elementType][action];
    case 'station': return STATION_MAINTENANCE_ACTIONS[elementType][action];
  }
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';
.flex {
  display: flex;
  em {
    margin: 0;
  }
}

.group-name {
  font-weight: normal;
  margin-bottom: bu(2);
}

.info-button {
  margin: 0 bu(1);
}

.work-order {
  margin-bottom: bu(1.5);
}

strong + .button {
  margin: bu(.5) bu(1);
  &--mobile-no-margin {
    @include breakpoint(small) {
      margin: 0;
    }
    @include breakpoint(medium) {
      margin: bu(.5) bu(1);
    }
  }
}

.det-mechs-tooltip {
  background-color: map-get($background-color, dark);
  width: bu(40);
  border-radius: map-get($border-radius, mega);
  padding: bu(2.5) bu(6) bu(2.5) bu(3.5);
  box-shadow: map-get($box-shadow, popover);
  margin-top: bu(1.5);

  &::before {
    content: '';
    width: 0;
    height: 0;
    position: absolute;
    top: bu(-1.5);
    left: calc(50% - #{bu(1.5)});
    border: bu(1.5) solid transparent;
    border-bottom-color: map-get($background-color, dark);
  }

  button {
    position: absolute;
    top: bu(1.5);
    right: bu(2);
  }

  .det-mechs-title {
    color: map-get($foreground-color, default);
    margin-bottom: bu(1);
  }

  .det-mechs-list {
    list-style: initial;
    margin-left: bu(2);

    li {
      color: map-get($foreground-color, dark);
      margin-bottom: bu(0.5);
    }
  }
}
</style>
